import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Info, Lock, ChevronDown, ChevronLeft, TrendingUp, Wallet, Users, Gauge, AlertTriangle, Sparkles, ArrowRight, Calculator } from "lucide-react";
import Seo, { faqSchema, breadcrumbSchema } from "../components/Seo";
import { Reveal } from "../components/common";
import { publicApi } from "../lib/api";
import { computeMetrics, selectAttention, fmtValue, fmtCurrency, METRICS } from "../lib/numbersCalc";
import { WhatIf, ActionPlan, LeadModal, Charts } from "../components/CalculatorExtras";

const SECTION_ICON = { profitability: TrendingUp, cash: Wallet, customers: Users, performance: Gauge };

const FAQS = [
  { question: "What is gross profit margin?", answer: "Gross profit margin is your gross profit divided by revenue, shown as a percentage. It tells you how much of every £1 of sales is left after the direct costs of delivering your product or service." },
  { question: "How do you calculate break-even turnover?", answer: "Break-even turnover is your fixed overheads divided by your gross profit margin (as a decimal). It's the level of revenue at which your business covers all its costs and makes neither a profit nor a loss." },
  { question: "What are debtor days?", answer: "Debtor days estimate the average number of days your customers take to pay you: trade debtors divided by revenue, multiplied by the number of days in the period. Lower debtor days means cash reaches your bank faster." },
  { question: "What is cash runway?", answer: "Cash runway estimates how many months your available cash would cover your operating costs at current levels. It's a simple gauge of financial resilience if income paused." },
  { question: "What is customer concentration?", answer: "Customer concentration is the share of your total revenue that comes from your single largest customer. The higher it is, the more exposed your business is if that customer leaves." },
];

// Format a number input as the user types, keeping the raw digits usable for calculation.
const cleanInput = (raw) => raw.replace(/[^0-9.\-]/g, "");

export default function BusinessNumbersCalculator() {
  const [cfg, setCfg] = useState(null);
  const [inputs, setInputs] = useState({});
  const [period, setPeriod] = useState("last_12_months");
  const [customDays, setCustomDays] = useState("365");
  const [vat, setVat] = useState("exclude");
  const [started, setStarted] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [plan, setPlan] = useState([]);
  const [leadOpen, setLeadOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [step, setStep] = useState(0);
  const [wizardDone, setWizardDone] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  useEffect(() => {
    publicApi.calculatorConfig().then(setCfg).catch(() => setCfg(null));
  }, []);

  const setInput = (key, val) => {
    if (!started) { setStarted(true); publicApi.trackCalculator("start"); }
    setInputs((f) => ({ ...f, [key]: cleanInput(val) }));
  };

  const results = useMemo(() => computeMetrics(inputs, period, customDays), [inputs, period, customDays]);
  const attention = useMemo(() => selectAttention(results), [results]);
  const okCount = useMemo(() => Object.keys(results).filter((k) => !k.startsWith("_") && results[k].status === "ok").length, [results]);

  const aiEnabled = !!(cfg && cfg.ai_summary_enabled);
  const [aiSummary, setAiSummary] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const facts = useMemo(() => {
    if (!cfg) return [];
    return METRICS.filter((m) => (cfg.metrics?.[m.key]?.visible !== false) && results[m.key]?.status === "ok")
      .map((m) => `${cfg.metrics[m.key].name}: ${fmtValue(m.format, results[m.key].value)}`);
  }, [results, cfg]);
  const factsKey = facts.join("|");
  useEffect(() => {
    if (!aiEnabled || facts.length < 4) { setAiSummary(""); return; }
    setAiLoading(true);
    const t = setTimeout(() => {
      publicApi.calculatorAiSummary(facts)
        .then((r) => setAiSummary(r?.summary || ""))
        .catch(() => setAiSummary(""))
        .finally(() => setAiLoading(false));
    }, 1400);
    return () => { clearTimeout(t); setAiLoading(false); };
  }, [aiEnabled, factsKey]); // eslint-disable-line

  if (!cfg) {
    return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading calculator…</div>;
  }

  const inputGroups = cfg.inputs.reduce((acc, f) => { (acc[f.group] = acc[f.group] || []).push(f); return acc; }, {});
  const metricCopy = cfg.metrics || {};
  const visibleMetrics = METRICS.filter((m) => metricCopy[m.key] && metricCopy[m.key].visible !== false);

  const workbookHref = cfg.workbook_guide?.file_url || cfg.workbook_guide ? "/insights#guides" : "/insights#guides";
  const summary = buildSummary(results, cfg);
  const displaySummary = aiSummary || summary;

  const periodLabel = period === "custom" ? `Custom period (${customDays} days)` : period === "latest_financial_year" ? "Latest financial year" : "Last 12 months";
  const buildSnapshot = () => ({
    period_label: `${periodLabel} · ${vat === "include" ? "incl. VAT" : "excl. VAT"}`,
    metrics: visibleMetrics.filter((m) => results[m.key]?.status === "ok").map((m) => ({
      name: metricCopy[m.key].name,
      value: fmtValue(m.format, results[m.key].value),
      means: (metricCopy[m.key].means || "").replace("{value}", fmtValue(m.format, results[m.key].value)),
    })),
    summary: displaySummary,
    attention: attention.map((a) => cfg.attention[a.key]).filter(Boolean),
    scenarios,
    action_plan: plan.filter((p) => (p.action || "").trim()),
    charts: (() => {
      const v = results._inputs || {};
      const gp = results.gross_profit?.status === "ok" ? results.gross_profit.value : null;
      const be = results.breakeven_annual?.status === "ok" ? results.breakeven_annual.value : null;
      const num = (x) => (x === null || x === undefined || Number.isNaN(x) ? null : x);
      return {
        revenue: num(v.turnover), direct_costs: num(v.direct_costs), gross_profit: num(gp),
        overheads: num(v.total_overheads), net_profit: num(v.net_profit), breakeven_annual: num(be),
        largest_customer: num(v.largest_customer_revenue),
        other_customers: (v.turnover != null && v.largest_customer_revenue != null) ? Math.max(0, v.turnover - v.largest_customer_revenue) : null,
      };
    })(),
    inputs,
  });
  const openLead = () => { publicApi.trackCalculator("email_capture_open"); setLeadOpen(true); };

  const renderField = (f) => (
    <div key={f.key}>
      <label className="flex items-center gap-1.5 text-xs font-semibold text-navy mb-1">
        {f.label}
        {f.help && (
          <span className="group relative inline-flex">
            <Info size={13} className="text-slate-400 cursor-help" />
            <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 w-56 z-20 opacity-0 group-hover:opacity-100 transition-opacity bg-navy text-white text-[11px] leading-snug rounded-lg px-3 py-2 shadow-lg">{f.help}</span>
          </span>
        )}
      </label>
      <div className="relative">
        {f.vat && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>}
        <input
          data-testid={`calc-input-${f.key}`}
          value={inputs[f.key] || ""}
          onChange={(e) => setInput(f.key, e.target.value)}
          inputMode="decimal"
          placeholder="0"
          className={`${isMobile ? inputClsLg : inputCls} ${f.vat ? "pl-7" : ""}`}
        />
      </div>
    </div>
  );

  const periodCard = (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <h2 className="font-display font-bold text-navy mb-3">Your figures</h2>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="block text-xs font-semibold text-navy mb-1">Financial period</label>
          <select data-testid="calc-period" value={period} onChange={(e) => setPeriod(e.target.value)} className={selCls}>
            <option value="last_12_months">Last 12 months</option>
            <option value="latest_financial_year">Latest financial year</option>
            <option value="custom">Custom period</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-navy mb-1">VAT basis</label>
          <select data-testid="calc-vat" value={vat} onChange={(e) => setVat(e.target.value)} className={selCls}>
            <option value="exclude">Excluding VAT</option>
            <option value="include">Including VAT</option>
          </select>
        </div>
      </div>
      {period === "custom" && (
        <div className="mb-4">
          <label className="block text-xs font-semibold text-navy mb-1">Number of days in this period</label>
          <input data-testid="calc-custom-days" value={customDays} onChange={(e) => setCustomDays(cleanInput(e.target.value))} className={inputCls} inputMode="numeric" />
        </div>
      )}
      {vat === "include" && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
          Figures including VAT can distort margins. Where possible, enter P&L figures excluding VAT.
        </p>
      )}
    </div>
  );

  const groupCards = Object.entries(inputGroups).map(([group, fields]) => (
    <div key={group} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
      <h3 className="font-display font-bold text-navy text-sm mb-3">{group}</h3>
      <div className="space-y-3">{fields.map(renderField)}</div>
    </div>
  ));

  const inputCards = [periodCard, ...groupCards];
  const stepCount = inputCards.length;
  const pct = Math.round(((step + 1) / stepCount) * 100);

  return (
    <div className="bg-slate-50 min-h-screen">
      <Seo
        title="Free Key Business Numbers Calculator"
        description="Calculate gross margin, net margin, break-even, debtor days, cash runway, customer concentration and other key business metrics with the free CASS Key Business Numbers Calculator."
        jsonLd={[faqSchema(FAQS), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Key Business Numbers Calculator", path: "/business-numbers-calculator" }])]}
      />

      {/* Hero */}
      <section className="bg-navy text-white pt-16 pb-12 md:pt-20 md:pb-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <span className="inline-flex items-center gap-2 text-orange font-bold text-sm tracking-widest uppercase mb-3"><Calculator size={16} /> Interactive tool</span>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl leading-tight max-w-3xl" data-testid="calc-title">{cfg.title}</h1>
          <p className="text-lg text-slate-300 mt-3 font-semibold">{cfg.subtitle}</p>
          <p className="text-base text-slate-400 mt-4 max-w-2xl leading-relaxed">{cfg.intro}</p>
          {cfg.workbook_note && <p className="text-sm text-slate-400 mt-4 italic">{cfg.workbook_note}</p>}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-14">
        <div className="grid lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)] gap-8 items-start">
          {/* ── Inputs (desktop list / mobile wizard) ── */}
          <div className={`lg:sticky lg:top-6 space-y-5 ${isMobile && wizardDone ? "hidden" : ""}`} data-testid="calc-inputs">
            {isMobile && !wizardDone && (
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4" data-testid="calc-wizard-progress">
                <div className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-2">
                  <span>Step {step + 1} of {stepCount}</span><span>{pct}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-orange transition-all" style={{ width: `${pct}%` }} /></div>
              </div>
            )}

            {inputCards.map((card, i) => (
              <div key={i} className={(!isMobile || i === step) ? "" : "hidden"}>{card}</div>
            ))}

            {isMobile && !wizardDone && (
              <div className="flex items-center gap-3 pt-1" data-testid="calc-wizard-nav">
                <button disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="calc-wizard-back"
                  className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full text-sm font-bold text-navy bg-white border border-slate-200 disabled:opacity-40">
                  <ChevronLeft size={16} /> Back
                </button>
                {step < stepCount - 1 ? (
                  <button onClick={() => setStep((s) => Math.min(stepCount - 1, s + 1))} data-testid="calc-wizard-next"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full text-sm font-bold text-white bg-navy">
                    Next <ArrowRight size={16} />
                  </button>
                ) : (
                  <button onClick={() => { setWizardDone(true); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="calc-wizard-finish"
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-full text-sm font-bold text-white bg-orange">
                    See my results <ArrowRight size={16} />
                  </button>
                )}
              </div>
            )}
            {isMobile && !wizardDone && okCount > 0 && (
              <button onClick={() => { setWizardDone(true); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="w-full text-center text-xs font-semibold text-teal py-1" data-testid="calc-wizard-skip">
                Skip ahead to my results ({okCount} numbers ready)
              </button>
            )}
          </div>

          {/* ── Dashboard ── */}
          <div className={`space-y-8 ${isMobile && !wizardDone ? "hidden" : ""}`} data-testid="calc-dashboard">
            {isMobile && wizardDone && (
              <button onClick={() => { setWizardDone(false); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="calc-edit-figures"
                className="inline-flex items-center gap-1.5 text-sm font-bold text-navy bg-white border border-slate-200 px-4 py-2 rounded-full">
                <ChevronLeft size={15} /> Edit my figures
              </button>
            )}
            {okCount === 0 ? (
              <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-10 text-center">
                <Calculator size={30} className="mx-auto text-slate-300 mb-3" />
                <p className="font-display font-bold text-navy text-lg">Your Business Numbers Dashboard</p>
                <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">Start entering your figures on the left and your key numbers will appear here instantly — with plain-English explanations of what each one means.</p>
              </div>
            ) : (
              <>
                {/* Summary */}
                {displaySummary && (
                  <div className="bg-navy text-white rounded-2xl p-6 md:p-7" data-testid="calc-summary">
                    <h2 className="font-display font-extrabold text-xl flex items-center gap-2 mb-3">
                      <Sparkles size={18} className="text-orange" /> {cfg.summary_heading}
                      {aiSummary && <span className="text-[10px] font-bold uppercase tracking-widest bg-orange/20 text-orange px-2 py-0.5 rounded-full" data-testid="calc-summary-ai-badge">AI</span>}
                    </h2>
                    <p className="text-slate-200 leading-relaxed" data-testid="calc-summary-text">{displaySummary}</p>
                    {aiLoading && <p className="text-xs text-slate-400 mt-2 italic">Refining your summary…</p>}
                    <button onClick={openLead} className="mt-5 inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-5 py-2.5 rounded-full transition-colors" data-testid="calc-get-report">
                      <Sparkles size={15} /> Get my full report (PDF)
                    </button>
                  </div>
                )}

                {/* Attention */}
                {attention.length > 0 && (
                  <div className="bg-orange/5 border border-orange/20 rounded-2xl p-6" data-testid="calc-attention">
                    <h2 className="font-display font-extrabold text-lg text-navy flex items-center gap-2 mb-4"><AlertTriangle size={18} className="text-orange" /> {cfg.attention_heading}</h2>
                    <div className="space-y-3">
                      {attention.map((a) => (
                        <div key={a.key} className="flex gap-3" data-testid={`calc-attention-${a.key}`}>
                          <span className="w-1.5 rounded-full bg-orange shrink-0" />
                          <p className="text-sm text-slate-700 leading-relaxed">{cfg.attention[a.key]}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Charts */}
                <Charts results={results} />

                {/* Metric sections */}
                {cfg.sections.map((sec) => {
                  const Icon = SECTION_ICON[sec.key] || TrendingUp;
                  const metricsHere = visibleMetrics.filter((m) => m.section === sec.key);
                  const okHere = metricsHere.filter((m) => results[m.key]?.status === "ok");
                  const lockedHere = metricsHere.filter((m) => results[m.key]?.status !== "ok");
                  if (okHere.length === 0 && lockedHere.length === metricsHere.length && okCount > 0 && !metricsHere.some((m) => results[m.key]?.status === "ok")) {
                    // still show section header with locked hints
                  }
                  return (
                    <div key={sec.key} data-testid={`calc-section-${sec.key}`}>
                      <div className="flex items-center gap-2.5 mb-4">
                        <span className="w-9 h-9 rounded-xl bg-navy/5 text-navy flex items-center justify-center"><Icon size={18} /></span>
                        <div>
                          <h2 className="font-display font-extrabold text-navy text-lg leading-tight">{sec.title}</h2>
                          <p className="text-xs text-slate-400">{sec.intro}</p>
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {okHere.map((m) => (
                          <MetricCard key={m.key} def={m} res={results[m.key]} copy={metricCopy[m.key]} />
                        ))}
                      </div>
                      {lockedHere.length > 0 && (
                        <LockedRow defs={lockedHere} results={results} copy={metricCopy} inputsCfg={cfg.inputs} />
                      )}
                    </div>
                  );
                })}

                {/* What-if modelling */}
                <WhatIf results={results} cfg={cfg} onScenarios={setScenarios} />

                {/* 90-day action plan */}
                <ActionPlan attention={attention} cfg={cfg} onChange={setPlan} />
              </>
            )}

            {/* Cross-links */}
            {cfg.cross_links?.length > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="calc-cross-links">
                <h3 className="font-display font-bold text-navy mb-1">Go further</h3>
                <p className="text-xs text-slate-400 mb-4">Your numbers tell you what's happening financially. These tools help you understand the wider picture.</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {cfg.cross_links.map((l, i) => (
                    <Link key={i} to={l.href} className="block rounded-xl border border-slate-100 hover:border-orange/40 hover:shadow-sm p-4 transition-all">
                      <p className="font-semibold text-navy text-sm flex items-center gap-1">{l.label} <ArrowRight size={13} className="text-orange" /></p>
                      <p className="text-xs text-slate-400 mt-1 leading-snug">{l.blurb}</p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 bg-navy text-white rounded-3xl p-8 md:p-12 text-center" data-testid="calc-cta">
          <h2 className="font-display font-extrabold text-2xl md:text-3xl max-w-2xl mx-auto leading-tight">{cfg.cta_heading}</h2>
          <p className="text-slate-300 mt-4 max-w-2xl mx-auto">{cfg.cta_body}</p>
          <div className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center mt-6">
            <Link to={cfg.cta_primary_link} onClick={() => publicApi.trackCalculator("cta_click")} className="inline-flex items-center justify-center text-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-6 py-3.5 rounded-2xl transition-colors w-full sm:w-80 min-h-[56px]" data-testid="calc-cta-primary">{cfg.cta_primary_label} <ArrowRight size={16} className="shrink-0" /></Link>
            <a href={cfg.workbook_guide?.file_url || workbookHref} onClick={() => publicApi.trackCalculator("workbook_download")} target={cfg.workbook_guide?.file_url ? "_blank" : undefined} rel="noopener noreferrer" className="inline-flex items-center justify-center text-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-6 py-3.5 rounded-2xl transition-colors w-full sm:w-80 min-h-[56px] leading-tight" data-testid="calc-cta-workbook">{cfg.cta_secondary_label}</a>
          </div>
        </div>

        <p className="text-xs text-slate-400 mt-8 max-w-3xl mx-auto text-center leading-relaxed" data-testid="calc-disclaimer">{cfg.disclaimer}</p>
      </div>

      <LeadModal open={leadOpen} onClose={() => setLeadOpen(false)} cfg={cfg} buildSnapshot={buildSnapshot} />
    </div>
  );
}

// ── Rule-based personalised summary ──
function buildSummary(r, cfg) {
  const parts = [];
  const nm = r.net_margin, be = r._headroom, dd = r.debtor_days, cc = r.customer_concentration, cr = r.cash_runway;
  if (nm?.status === "ok") {
    if (nm.value >= 0) parts.push(`Your business is profitable, keeping around ${Math.round(nm.value)}p of profit from every £1 of revenue.`);
    else parts.push(`Your business is currently making a loss at the net level, so profitability is the first thing worth looking at.`);
  }
  if (be?.status === "ok") {
    parts.push(be.amount >= 0
      ? `You're operating above your estimated break-even level, with headroom of about ${fmtCurrency(be.amount)}.`
      : `Your revenue currently appears to be below your estimated break-even level.`);
  }
  if (dd?.status === "ok" && dd.value > 45) parts.push(`A meaningful amount of cash is tied up in customer invoices, with debtor days around ${Math.round(dd.value)}.`);
  if (cc?.status === "ok" && cc.value > 25) parts.push(`Your largest customer represents ${Math.round(cc.value)}% of revenue, which is worth keeping an eye on.`);
  if (cr?.status === "ok" && cr.value < 3) parts.push(`Your estimated cash runway is under three months, so cash resilience may be worth strengthening.`);
  if (parts.length < 1) return "";
  return parts.join(" ");
}

// ── Metric card ──
function MetricCard({ def, res, copy }) {
  const [open, setOpen] = useState(false);
  const val = fmtValue(def.format, res.value);
  const means = (copy.means || "").replace("{value}", val);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col" data-testid={`calc-metric-${def.key}`}>
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">{copy.name}</p>
      <p className="font-display font-extrabold text-3xl text-navy mt-1" data-testid={`calc-metric-value-${def.key}`}>{val}</p>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{means}</p>
      {res.assumption && <p className="text-[11px] text-slate-400 mt-1.5 italic">{res.assumption}</p>}
      <button onClick={() => setOpen((o) => !o)} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-orange hover:text-orange-dark self-start" data-testid={`calc-metric-toggle-${def.key}`}>
        Understand this number <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 pt-3 border-t border-slate-100 space-y-2">
          <div><p className="text-[11px] font-bold text-navy uppercase tracking-wide">Why it matters</p><p className="text-sm text-slate-600 leading-relaxed">{copy.matters}</p></div>
          <div><p className="text-[11px] font-bold text-navy uppercase tracking-wide">What to consider</p><p className="text-sm text-slate-600 leading-relaxed">{copy.consider}</p></div>
        </div>
      )}
    </div>
  );
}

// ── Locked metrics (what to add to unlock) ──
function LockedRow({ defs, results, copy, inputsCfg }) {
  const labelFor = (k) => {
    if (k.includes("|")) return k.split("|").map((x) => inputsCfg.find((i) => i.key === x)?.label || x).join(" or ");
    return inputsCfg.find((i) => i.key === k)?.label || k;
  };
  return (
    <div className="mt-4 bg-slate-100/70 rounded-2xl p-4" data-testid="calc-locked-row">
      <p className="text-xs font-semibold text-slate-500 flex items-center gap-1.5 mb-2"><Lock size={12} /> Add a little more to unlock:</p>
      <div className="flex flex-wrap gap-2">
        {defs.map((m) => {
          const res = results[m.key];
          const need = res?.status === "missing" ? (res.missing || []).map(labelFor).join(", ") : (res?.note || "");
          return (
            <span key={m.key} className="text-[11px] bg-white border border-slate-200 rounded-full px-3 py-1.5 text-slate-500" data-testid={`calc-locked-${m.key}`} title={need}>
              <strong className="text-navy font-semibold">{copy[m.key]?.name}</strong>{need ? ` — add ${need}` : ""}
            </span>
          );
        })}
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-orange/40";
const inputClsLg = "w-full rounded-xl border border-slate-200 px-4 py-3.5 text-lg text-navy focus:outline-none focus:ring-2 focus:ring-orange/40";
const selCls = "w-full rounded-xl border border-slate-200 px-2.5 py-2 text-sm text-navy bg-white focus:outline-none focus:ring-2 focus:ring-orange/40";
